import React from 'react';
import Icon from 'components/AppIcon';

const MarketPreview = ({ marketData, categories }) => {
  const getCategoryLabel = (categoryId) => {
    return categories?.find(cat => cat?.id === categoryId)?.label || categoryId;
  };

  const getCategoryIcon = (categoryId) => {
    return categories?.find(cat => cat?.id === categoryId)?.icon || 'HelpCircle';
  };

  const formatDate = (date, time) => {
    if (!date) return 'Select end date';
    const dateObj = new Date(`${date}T${time || '23:59'}`);
    const now = new Date();
    const diffTime = dateObj - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Past date';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day';
    return `${diffDays} days`;
  };

  return (
    <div className="bg-surface border border-border rounded-trading shadow-trading-card p-6 sticky top-24">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-body-semibold text-text-primary">Live Preview</h3>
        <Icon name="Eye" size={16} className="text-text-secondary" />
      </div>

      {/* Market Card Preview */}
      <div className="bg-surface-hover border border-border rounded-trading p-4 transition-all duration-trading-normal">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-gradient-primary rounded-full flex items-center justify-center glow-primary">
              <Icon 
                name={marketData?.category ? getCategoryIcon(marketData?.category) : 'HelpCircle'} 
                size={12} 
                color="white" 
              />
            </div>
            <span className="text-xs bg-primary-900 text-primary-300 px-2 py-0.5 rounded">
              {marketData?.category ? getCategoryLabel(marketData?.category) : 'Category'}
            </span>
          </div>
          <span className="text-xs text-success flex items-center">
            <Icon name="Clock" size={10} className="mr-1" />
            {formatDate(marketData?.endDate, marketData?.endTime)}
          </span>
        </div>

        <h4 className="font-body-medium text-text-primary mb-2 text-sm line-clamp-2 min-h-[2.5rem]">
          {marketData?.title || 'Your market question will appear here...'}
        </h4>
        
        {marketData?.description && (
          <p className="text-xs text-text-secondary mb-3 line-clamp-2">
            {marketData?.description}
          </p>
        )}

        {/* Tags Preview */}
        {marketData?.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {marketData?.tags?.slice(0, 3)?.map((tag, index) => (
              <span
                key={index}
                className="text-xs bg-surface text-text-secondary px-2 py-0.5 rounded"
              >
                {tag}
              </span>
            ))}
            {marketData?.tags?.length > 3 && (
              <span className="text-xs text-text-secondary">+{marketData?.tags?.length - 3}</span>
            )}
          </div>
        )}

        {/* Outcome Tokens Preview */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="bg-surface rounded p-2 text-center">
            <p className="text-xs text-text-secondary mb-1">Yes</p>
            <p className="font-data text-sm text-success">$0.50</p>
          </div>
          <div className="bg-surface rounded p-2 text-center">
            <p className="text-xs text-text-secondary mb-1">No</p>
            <p className="font-data text-sm text-error">$0.50</p>
          </div>
        </div>

        {/* Market Stats Preview */}
        <div className="flex items-center justify-between text-xs">
          <span className="text-text-secondary">Volume: $0</span>
          <div className="flex items-center space-x-2">
            <span className="text-text-secondary">
              Fee: {marketData?.creatorFee || 2}%
            </span>
            <span className="text-primary">Preview</span>
          </div>
        </div>
      </div>

      {/* Preview Stats */}
      <div className="mt-4 space-y-3">
        <div className="flex items-center justify-between py-2 border-b border-border">
          <span className="text-xs text-text-secondary">Question Length</span>
          <span className={`text-xs font-data ${
            marketData?.title?.length >= 10 && marketData?.title?.length <= 200 
              ? 'text-success' :'text-warning'
          }`}>
            {marketData?.title?.length || 0}/200
          </span>
        </div>
        
        <div className="flex items-center justify-between py-2 border-b border-border">
          <span className="text-xs text-text-secondary">Description</span>
          <span className={`text-xs font-data ${
            marketData?.description?.length >= 50 
              ? 'text-success' :'text-warning'
          }`}>
            {marketData?.description?.length || 0}/2000
          </span>
        </div>

        <div className="flex items-center justify-between py-2 border-b border-border">
          <span className="text-xs text-text-secondary">End Date</span>
          <span className={`text-xs font-data ${
            marketData?.endDate 
              ? 'text-success' :'text-warning'
          }`}>
            {marketData?.endDate ? 'Set' : 'Required'}
          </span>
        </div>

        <div className="flex items-center justify-between py-2">
          <span className="text-xs text-text-secondary">Resolution Criteria</span>
          <span className={`text-xs font-data ${
            marketData?.resolutionCriteria?.length >= 20 
              ? 'text-success' :'text-warning'
          }`}>
            {marketData?.resolutionCriteria?.length >= 20 ? 'Complete' : 'Needed'}
          </span>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="grid grid-cols-2 gap-2">
          <div className="text-center p-2 bg-surface-hover rounded text-xs">
            <Icon name="TrendingUp" size={12} className="mx-auto mb-1 text-text-secondary" />
            <span className="text-text-secondary">Potential</span>
          </div>
          <div className="text-center p-2 bg-surface-hover rounded text-xs">
            <Icon name="Users" size={12} className="mx-auto mb-1 text-text-secondary" />
            <span className="text-text-secondary">Traders</span>
          </div>
        </div>
      </div>

      {/* Readiness Indicator */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-text-secondary">Completion</span>
          <span className="text-xs font-data text-text-primary">
            {Math.round(
              ([
                marketData?.title?.length >= 10,
                marketData?.description?.length >= 50,
                marketData?.category,
                marketData?.endDate,
                marketData?.resolutionCriteria?.length >= 20
              ]?.filter(Boolean)?.length / 5) * 100
            )}%
          </span>
        </div>
        <div className="w-full bg-surface-hover rounded-full h-2">
          <div
            className="bg-gradient-primary h-2 rounded-full transition-all duration-trading-normal glow-primary"
            style={{
              width: `${Math.round(
                ([
                  marketData?.title?.length >= 10,
                  marketData?.description?.length >= 50,
                  marketData?.category,
                  marketData?.endDate,
                  marketData?.resolutionCriteria?.length >= 20
                ]?.filter(Boolean)?.length / 5) * 100
              )}%`
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default MarketPreview;